
import { mapState, mapGetters } from 'vuex';
import SocialMediaType from '@/enums/SocialMediaType';
import PaginatedReviewResourceView from '@/mixins/PaginatedReviewResourceView';
import ReviewMediaSection from './components/ReviewMediaSection';
import { POPUP_PER } from '@/constants/PARAMS';

export default {
  i18nComponentKey: 'ReviewsShow.ReviewsShowMobile',
  name: 'ReviewsShowMobile',
  components: { ReviewMediaSection },
  mixins: [PaginatedReviewResourceView],
  data() {
    return { previousScrollHeightMinusScrollTop: 0, isLoadingPrev: false };
  },
  computed: {
    ...mapState(['unlinkable']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('review', ['mediaIndex']),
    ...mapState('widget', ['reviewId']),
    ...mapState('window', ['windowService']),
    ...mapGetters(['fromTargetWidget']),
    ...mapGetters('widget', [
      'shortsReviewPopupUrl',
      'nthReviewWidgetStylePopup'
    ]),
    ...mapGetters('review', ['getCurrentPage']),
    SocialMediaType: () => SocialMediaType,
    defaultResourceParams() {
      const params = {
        fields: [
          'reviews.customer_properties',
          'reviews.display_user_options',
          'reviews.comments',
          'reviews.evaluation_properties',
          this.nthReviewWidgetStylePopup ? 'reviews.with_parent_reviews' : ''
        ]
          .filter(v => v)
          .join(',')
      };

      if (this.fromTargetWidget && this.reviewId) {
        params.review_id = this.reviewId;
        params.per = POPUP_PER;
      }

      return params;
    },
    initialMediaIndex() {
      return this.mediaIndex ? this.mediaIndex - 1 : 0;
    }
  },
  created() {
    this.scrollAnchorOffset = null;
    this.scrollAnchorObserver = null;
    this.scrollAnchorTimerId = null;
  },
  beforeDestroy() {
    if (this.scrollAnchorObserver) this.scrollAnchorObserver.disconnect();
    if (this.scrollAnchorTimerId) clearTimeout(this.scrollAnchorTimerId);
  },
  methods: {
    onReady() {
      const clickedReviewIndex = this.reviews.findIndex(
        review => review.id.toString() === this.reviewId
      );
      if (clickedReviewIndex < 0) {
        if (this.reviews.length) {
          this.windowService.alert(this.$t('filtered_review_mismatch'));
        } else {
          this.windowService.alert(this.$t('filtered_review_none'));
          this.onClose();
          return;
        }
      }
      const targetReviewId =
        clickedReviewIndex < 0 ? this.reviews[0].id : this.reviewId;

      this.$emit('ready');

      // HACK: 커스텀 CSS 적용 후 스크롤 위치 조정
      this.$nextTick(() => {
        this.scrollToReview(targetReviewId);
        this.setupScrollAnchor();
      });
    },
    setupScrollAnchor() {
      // iOS CSS overflow-anchor 미지원에 따른 스크롤 재조정
      if (!window.ResizeObserver) return;
      if (CSS.supports('overflow-anchor', 'auto')) return;

      const containerEl = this.getContainerEl();
      if (!containerEl) return;

      const targetEl = containerEl.querySelector(`.review_${this.reviewId}`);
      if (!targetEl) return;

      const reviews = Array.from(
        containerEl.querySelectorAll('.ReviewsShowMobile__review')
      );
      const reviewsAbove = reviews.slice(0, reviews.indexOf(targetEl));
      if (reviewsAbove.length === 0) return;

      if (this.scrollAnchorObserver) this.scrollAnchorObserver.disconnect();
      if (this.scrollAnchorTimerId) clearTimeout(this.scrollAnchorTimerId);

      const observer = new ResizeObserver(() => {
        this.scrollToReview(this.reviewId);
      });
      this.scrollAnchorObserver = observer;
      reviewsAbove.forEach(el => observer.observe(el));
      this.scrollAnchorTimerId = setTimeout(() => observer.disconnect(), 5000);
    },
    recordScrollPosition() {
      const node = this.getContainerEl();
      this.previousScrollHeightMinusScrollTop =
        node.scrollHeight - node.scrollTop;

      // overflow-anchor 미지원(iOS): 타겟 리뷰의 컨테이너 기준 상대 위치를 기록
      if (CSS.supports('overflow-anchor', 'auto')) return;

      const targetEl = node.querySelector(`.review_${this.reviewId}`);
      this.scrollAnchorOffset = targetEl
        ? this.getRelativeTop(targetEl, node)
        : null;
    },
    restoreScrollPosition() {
      const node = this.getContainerEl();
      // overflow-anchor 미지원(iOS): 기록해둔 상대 위치로 스크롤 복원
      if (this.scrollAnchorOffset !== null) {
        const targetEl = node.querySelector(`.review_${this.reviewId}`);
        if (targetEl) {
          node.scrollTop +=
            this.getRelativeTop(targetEl, node) - this.scrollAnchorOffset;
          this.scrollAnchorOffset = null;
          return;
        }
      }
      node.scrollTop =
        node.scrollHeight - this.previousScrollHeightMinusScrollTop;
    },
    prepend() {
      if (this.pagy.prev && !this.isLoadingPrev) {
        const node = this.getContainerEl();
        this.isLoadingPrev = true;
        this.recordScrollPosition();
        node.style.overflow = 'hidden';
        this.prependPage(this.pagy.prev).then(() => {
          this.$nextTick(() => {
            node.style.overflow = '';
            this.restoreScrollPosition();
            this.setupScrollAnchor();
            this.isLoadingPrev = false;
          });
        });
      }
    },
    append() {
      if (this.pagy.next) this.appendPage({ page: this.pagy.next });
    },
    scrollToReview(reviewId) {
      const containerEl = this.getContainerEl();
      if (!containerEl) return;

      const targetEl = containerEl.querySelector(`.review_${reviewId}`);
      if (!targetEl) return;

      const delta = this.getRelativeTop(targetEl, containerEl);
      if (Math.abs(delta) < 1) return;

      containerEl.scrollTop += delta;
    },
    getContainerEl() {
      return this.$refs.review_contents?.$el;
    },
    getRelativeTop(el, containerEl) {
      return (
        el.getBoundingClientRect().top - containerEl.getBoundingClientRect().top
      );
    },
    onClose() {
      this.windowService.closeFullscreenPopup();
    },
    onClickExpand(event, reviewId, reviewIndex, mediaIndex) {
      const url = this.shortsReviewPopupUrl(reviewId, mediaIndex, reviewIndex, {
        ...this.popupParams,
        page: this.getCurrentPage(reviewId),
        per: POPUP_PER,
        with_media: true,
        unlinkable: true
      });
      this.windowService.openFullscreenPopup(url, event.currentTarget);
    }
  }
};
